import { parse, print } from 'graphql'
import { AVAILABILITY_TIME_FRAGMENT } from 'customer-flows/gql/fragments/availability-time'
import { useQuery } from 'glimmer-apollo'
import { isDestroying, isDestroyed } from '@ember/destroyable'
import type { ApolloQueryResult } from '@apollo/client/core'
import { throwClassifiedFetchError } from 'customer-flows/gql/queries/availability-errors'

export type AvailabilityDatesInput = {
  availabilityRequest: AvailabilityRequestInput
}

export type AvailabilityDatesVariables = Omit<AvailabilityRequestInput, 'activityId'>

export type AvailabilityRequestInput = {
  activityId: string
  channelId?: string
  dateRange: string
  resourceOptionQuantities: Array<{
    quantity: number
    resourceOptionId?: string
  }>
}

export type AvailabilityDatesResponse = {
  availabilityDates: AvailabilityDatesSuccess | GenericError | NotFoundError
}

export type AvailabilityDatesSuccess = {
  __typename: 'AvailabilityDatesSuccess'
  availabilityDates: AvailabilityDate[]
}

export async function fetchAvailabilityDates(
  context: Object,
  variables: AvailabilityRequestInput,
  options?: { fetchOnlyDates?: boolean, lowPriority?: boolean }
): Promise<ApolloQueryResult<AvailabilityDatesResponse> | undefined> {
  if (isDestroying(context) || isDestroyed(context)) return undefined

  const queryToPerform = options?.fetchOnlyDates ? queryWithoutTimes : queryWithTimes

  const availabilityQuery = useQuery<AvailabilityDatesResponse, AvailabilityDatesInput>(context, () => [
    queryToPerform,
    {
      fetchPolicy: 'no-cache', // SO IMPORTANT, availability is HUGE and changes often. Without this the UI lags A LOT
      variables: {
        availabilityRequest: variables
      },
      context: {
        headers: options?.lowPriority ? {
          'X-Request-Priority': 'low'
        } : {}
      },
      onError: (error) => throwClassifiedFetchError('Failed to fetch availability', error)
    }
  ])

  return availabilityQuery.refetch()
}

const queryWithTimes = parse(`
  query AvailabilityDates($availabilityRequest: AvailabilityRequestInput!) {
    availabilityDates(availabilityRequest: $availabilityRequest) {
      ...on AvailabilityDatesSuccess {
        availabilityDates {
          date
          status
          priceRange {
            amount
            currency
          }
          ticketsAvailability {
            resourceOptionId
            spotsAvailable
          }
          availabilityTimes {
            ...AvailabilityTimeFields
          }
        }
      }
    }
  }
  ${print(AVAILABILITY_TIME_FRAGMENT)}
`)

const queryWithoutTimes = parse(`
  query AvailabilityDates($availabilityRequest: AvailabilityRequestInput!) {
    availabilityDates(availabilityRequest: $availabilityRequest) {
      ...on AvailabilityDatesSuccess {
        availabilityDates {
          date
          status
          priceRange {
            amount
            currency
          }
          ticketsAvailability {
            resourceOptionId
            spotsAvailable
          }
        }
      }
    }
  }
`)
